import {
  Dropzone,
  DropZoneArea,
  DropzoneDescription,
  DropzoneFileList,
  DropzoneFileListItem,
  DropzoneFileMessage,
  DropzoneMessage,
  DropzoneTrigger,
  useDropzone,
} from '@/components/ui/dropzone.tsx';
import { FileUpIcon, ArrowLeft } from 'lucide-react';
import { IconButton } from '@/components/ui/button.tsx';
import useImportScanStore from './hook/useImportScanStore';
import { useState } from 'react';
import { generateModelSnapshot } from '@modules/three/utils/modelSnapshot.ts';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

export function SelectScanFiles() {
  const navigate = useNavigate();
  const { upperJawFile, lowerJawFile, setUpperJawFile, setLowerJawFile, setImportPhase } = useImportScanStore();
  const [upperJawPreview, setUpperJawPreview] = useState<string | null>(null);
  const [lowerJawPreview, setLowerJawPreview] = useState<string | null>(null);

  // upper jaw dropzone
  const upperDropzone = useDropzone<File, string>({
    validation: {
      accept: { 'application/octet-stream': ['.ply', '.stl'] },
      maxFiles: 1,
    },
    shiftOnMaxFiles: true,
    onDropFile: async (file) => {
      setUpperJawFile(file);
      const imgUrl = await generateModelSnapshot(file, {
        width: 200,
        height: 200,
        useVertexColors: true,
      });
      setUpperJawPreview(imgUrl);
      return { status: 'success', result: file };
    },
    onRemoveFile: () => {
      setUpperJawFile(null);
    },
  });

  //  lower jaw dropzone
  const lowerDropzone = useDropzone<File, string>({
    validation: {
      accept: { 'application/octet-stream': ['.ply', '.stl'] },
      maxFiles: 1,
    },
    shiftOnMaxFiles: true,
    onDropFile: async (file) => {
      setLowerJawFile(file);
      const imgUrl = await generateModelSnapshot(file, {
        width: 200,
        height: 200,
        useVertexColors: true,
      });
      setLowerJawPreview(imgUrl);
      return { status: 'success', result: file };
    },
    onRemoveFile: () => {
      setLowerJawFile(null);
    },
  });

  const renderDropzone = (
    label: string,
    preview: string | null,
    dropzone: ReturnType<typeof useDropzone<File, string>>,
  ) => (
    <div className="flex flex-1 flex-col gap-4 rounded-3xl border border-content-highlight/40 bg-content-dark/60 p-6 text-white shadow-lg shadow-primary/15">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/50">{label}</p>
        <h3 className="text-lg font-semibold tracking-tight">Upload {label.toLowerCase()}</h3>
        <p className="text-sm text-white/55">Supported formats: .PLY, .STL</p>
      </div>

      <Dropzone {...dropzone}>
        <DropzoneDescription className="sr-only">Upload a .PLY or .STL file</DropzoneDescription>
        <DropzoneMessage />
        <DropZoneArea className="rounded-2xl border border-dashed border-content-highlight/40 bg-content/40 p-6 transition hover:border-primary/50 hover:bg-primary/5">
          <DropzoneTrigger className="flex h-64 w-full flex-col items-center justify-center gap-4 text-center">
            {preview ? (
              <img src={preview} alt={`${label} preview`} className="h-40 w-40 rounded-2xl object-contain" />
            ) : (
              <>
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                  <FileUpIcon className="h-6 w-6" />
                </span>
                <div className="space-y-1">
                  <h4 className="text-base font-semibold">Drop files or click to browse</h4>
                  <p className="text-sm text-white/60">We’ll generate a quick preview once uploaded.</p>
                </div>
              </>
            )}
          </DropzoneTrigger>
          {dropzone.fileStatuses.length > 0 && (
            <DropzoneFileList className="mt-4 space-y-2">
              {dropzone.fileStatuses.map((status) => (
                <DropzoneFileListItem
                  key={status.id}
                  file={status}
                  className="flex items-center justify-between rounded-2xl border border-content-highlight/30 bg-content-dark/80 px-4 py-3"
                >
                  <span className="truncate text-sm font-medium text-white/80">{status.file.name}</span>
                  <DropzoneFileMessage className="text-xs text-white/50" />
                </DropzoneFileListItem>
              ))}
            </DropzoneFileList>
          )}
        </DropZoneArea>
      </Dropzone>
    </div>
  );

  return (
    <section className="flex h-full w-full flex-col items-center justify-center p-6">
      <div className="flex w-full max-w-5xl flex-col gap-8 rounded-3xl border border-primary/40 bg-content/80 p-10 text-white shadow-2xl shadow-primary/20 backdrop-blur">
        <header className="flex flex-col gap-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.4em] text-primary/70">Import workflow</p>
          <h2 className="text-3xl font-semibold tracking-tight">Upload upper and lower jaw scans</h2>
          <p className="text-sm text-white/65">
            Upload both jaws to continue. You’ll review and adjust their orientation before completing the import.
          </p>
        </header>

        <div className="flex flex-col gap-6 lg:flex-row">
          {renderDropzone('Upper Jaw', upperJawPreview, upperDropzone)}
          {renderDropzone('Lower Jaw', lowerJawPreview, lowerDropzone)}
        </div>

        <footer className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <IconButton
            type="button"
            variant="ghost-light"
            leftIcon={ArrowLeft}
            className="sm:w-auto"
            onClick={() => navigate(-1)}
          >
            Back
          </IconButton>
          <IconButton
            type="button"
            className={cn('sm:w-auto', !(upperJawFile && lowerJawFile) && 'opacity-60')}
            disabled={!upperJawFile || !lowerJawFile}
            onClick={() => setImportPhase('rotationFix')}
          >
            Continue to alignment
          </IconButton>
        </footer>
      </div>
    </section>
  );
}
